import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Dialog, Input, LinkButton, Money } from '../../../components/ui';
import { Thumb } from '../../MenuItems/components/Thumb';
import type { ItemRecord, RestaurantInfo } from '../../MenuItems/types';
import { pickName } from '../../MenuItems/utils';

interface Props {
    open: boolean;
    onClose: () => void;
    items: ItemRecord[];
    /** Ids already in the category: shown checked and locked. */
    present: string[];
    lang: string;
    restaurant: RestaurantInfo | null;
    onAdd: (ids: string[]) => void;
    /** Link to create a new item; absent while the menu is unsaved. */
    createTo?: string;
}

export const AddItemsDialog = ({ open, onClose, items, present, lang, restaurant, onAdd, createTo }: Props) => {
    const { t } = useTranslation('menu');
    const [query, setQuery] = useState('');
    const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());

    const rows = useMemo(() => {
        const q = query.trim().toLocaleLowerCase();
        return items
            .map((item) => ({ item, name: pickName(item.translations, lang) || t('items.untitled') }))
            .filter((r) => !q || r.name.toLocaleLowerCase().includes(q));
    }, [items, query, lang, t]);

    const close = () => {
        setQuery('');
        setPicked(new Set());
        onClose();
    };
    const toggle = (id: string, on: boolean) =>
        setPicked((prev) => {
            const next = new Set(prev);
            if (on) next.add(id);
            else next.delete(id);
            return next;
        });

    return (
        <Dialog
            open={open}
            onClose={close}
            size="lg"
            title={t('addDialog.title')}
            footer={
                <>
                    <span role="status" className="self-center text-small text-muted sm:me-auto">
                        {t('addDialog.selected', { count: picked.size })}
                    </span>
                    <Button
                        disabled={picked.size === 0}
                        onClick={() => {
                            onAdd([...picked]);
                            close();
                        }}
                    >
                        {t('addDialog.add')}
                    </Button>
                </>
            }
        >
            <div className="flex flex-col gap-3 pb-2">
                <Input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    aria-label={t('addDialog.search')}
                    placeholder={t('addDialog.search')}
                    leading={<MagnifyingGlassIcon className="size-5" aria-hidden="true" />}
                />
                {items.length === 0 ? (
                    <p className="py-6 text-center text-small text-muted">{t('addDialog.noItems')}</p>
                ) : rows.length === 0 ? (
                    <p className="py-6 text-center text-small text-muted">{t('addDialog.noMatches')}</p>
                ) : (
                    <ul className="flex flex-col">
                        {rows.map(({ item, name }) => {
                            const already = present.includes(item.id);
                            return (
                                <li key={item.id} className="border-t border-hairline first:border-t-0">
                                    <Checkbox
                                        checked={already || picked.has(item.id)}
                                        disabled={already}
                                        onChange={(on) => toggle(item.id, on)}
                                        label={
                                            <span className="flex items-center gap-3">
                                                <Thumb url={item.imageUrl} />
                                                <span className="min-w-0 flex-1 truncate font-semibold">{name}</span>
                                                {restaurant && (
                                                    <Money
                                                        amount={item.price}
                                                        symbol={restaurant.currencySymbol}
                                                        position={restaurant.currencySymbolPosition}
                                                        className="text-muted"
                                                    />
                                                )}
                                            </span>
                                        }
                                    />
                                </li>
                            );
                        })}
                    </ul>
                )}
                {createTo && (
                    <LinkButton to={createTo} variant="link" className="self-start">
                        {t('addDialog.create')}
                    </LinkButton>
                )}
            </div>
        </Dialog>
    );
};
